/**
 * Bezier editor model (W2-05 easing-templates-raw; DESIGN.md §4.12 "Bezier tab", rpe-format.md F§6.7): parsing and
 * printing `cubic-bezier(...)`, snapping handles to 0.01, presets, and the collision check against the chart's
 * other Bezier curves.
 *
 * Collisions: Phira keeps ONE solver per rounded key of the handles (`Ease.bezierKey`, computed in f32 exactly as
 * Phira does: 0.01 quantisation, i16 / u16 wrapping). The first curve met in Phira's scan order creates the solver
 * with its exact handles, and every later curve with the same key is drawn with THOSE handles. Scan order: lines
 * in export order; per line every eventLayers layer in order (alpha, moveX, moveY, rotate; speed is not scanned),
 * then `extended` (paint, scaleX, scaleY, gif, incline, text, colour); events in time order.
 */

import { Ease } from '../../core/ease/index';
import type { BezierPoints } from '../../core/ease/types';
import type { ChartDoc, DocIndexes, KeyId, LineId, LineProp } from '../../core/model/types';

/** Snapping step of the handles (Phira's cache key resolution). */
export const BEZIER_SNAP = 0.01;

/** Rounds to the 0.01 grid (no float noise, no -0). */
export function snapHandle(v: number, step = BEZIER_SNAP): number {
  const r = Math.round(v / step) * step;
  const clean = Number(r.toFixed(6));
  return clean === 0 ? 0 : clean;
}

/**
 * A handle coordinate moved by `delta` (arrow-key nudge): on the 0.01 grid while `snap` is on, otherwise exactly
 * `v + delta` with only the float noise of the addition removed (0.2 + 0.1 gives 0.3, 0.255 + 0.01 gives 0.265);
 * a zero `delta` then leaves `v` exactly as it is.
 */
export function nudgeHandle(v: number, delta: number, snap: boolean): number {
  if (snap) return snapHandle(v + delta);
  if (delta === 0) return v;
  const clean = Number((v + delta).toPrecision(12));
  return clean === 0 ? 0 : clean;
}

export function clampX(x: number): number {
  return Math.min(Math.max(x, 0), 1);
}

/** Handles with x clamped to [0, 1] and, when `snap`, every coordinate on the 0.01 grid. */
export function normalizeHandles(p: BezierPoints, snap: boolean): BezierPoints {
  const s = (v: number): number => (snap ? snapHandle(v) : v);
  return [clampX(s(p[0])), s(p[1]), clampX(s(p[2])), s(p[3])];
}

/**
 * Reads `cubic-bezier(0.25, 0.1, 0.25, 1)`, `[0.25, 0.1, 0.25, 1]`, `0.25 0.1 0.25 1` or RPE's
 * `bezierPoints` JSON. Null unless exactly four finite numbers are found.
 */
export function parseCubicBezier(text: string): BezierPoints | null {
  const body = text
    .trim()
    .replace(/^"?bezierPoints"?\s*:\s*/i, '')
    .replace(/^cubic-bezier\s*\(/i, '')
    .replace(/^\[/, '')
    .replace(/[)\]]\s*;?\s*$/, '');
  const parts = body.split(/[\s,]+/).filter((x) => x !== '');
  if (parts.length !== 4) return null;
  const nums = parts.map((x) => (/^[-+]?(\d+\.?\d*|\.\d+)(e[-+]?\d+)?$/i.test(x) ? Number(x) : NaN));
  if (!nums.every((v) => Number.isFinite(v))) return null;
  return [nums[0] ?? 0, nums[1] ?? 0, nums[2] ?? 0, nums[3] ?? 0];
}

function fmt(v: number): string {
  const r = Number(v.toFixed(4));
  return String(r === 0 ? 0 : r);
}

export function formatCubicBezier(p: BezierPoints): string {
  return `cubic-bezier(${p.map(fmt).join(', ')})`;
}

/** Handles as a short readable list "0.25, 0.1, 0.25, 1". */
export function formatHandles(p: BezierPoints): string {
  return p.map(fmt).join(', ');
}

export function sameHandles(a: BezierPoints, b: BezierPoints): boolean {
  return a.every((v, i) => v === b[i]);
}

export interface BezierPreset {
  readonly id: 'ease' | 'easeIn' | 'easeOut' | 'easeInOut' | 'quintInOut' | 'expoInOut';
  readonly p: BezierPoints;
}

let presetCache: readonly BezierPreset[] | null = null;

/** CSS's named curves and the fitted curves Phira has but RPE cannot number. */
export function bezierPresets(): readonly BezierPreset[] {
  presetCache ??= [
    { id: 'ease', p: [0.25, 0.1, 0.25, 1] },
    { id: 'easeIn', p: [0.42, 0, 1, 1] },
    { id: 'easeOut', p: [0, 0, 0.58, 1] },
    { id: 'easeInOut', p: [0.42, 0, 0.58, 1] },
    { id: 'quintInOut', p: Ease.approxBezier('quint', 'inout').p },
    { id: 'expoInOut', p: Ease.approxBezier('expo', 'inout').p },
  ];
  return presetCache;
}

// ---------------------------------------------------------------------------------------------------------------
// Collisions
// ---------------------------------------------------------------------------------------------------------------

const LAYER_SCAN: readonly LineProp[] = ['alpha', 'moveX', 'moveY', 'rotate'];
const EXTENDED_SCAN: readonly LineProp[] = ['paint', 'scaleX', 'scaleY', 'gif', 'incline', 'text', 'color'];

/** Position of a key in Phira's Bezier scan, or null when Phira does not scan it (speed, effect variables). */
export function scanRank(doc: ChartDoc, idx: DocIndexes, key: KeyId): readonly number[] | null {
  const k = doc.keys.get(key);
  const t = k === undefined ? undefined : doc.tracks.get(k.track);
  if (k === undefined || t?.owner.kind !== 'line') return null;
  const { line, prop, layer } = t.owner;
  const li = idx.lineIndex(line);
  if (li < 0) return null;
  const lp = LAYER_SCAN.indexOf(prop);
  const ep = EXTENDED_SCAN.indexOf(prop);
  if (lp < 0 && ep < 0) return null;
  const beat = k.t.w + k.t.n / k.t.d;
  return lp >= 0 ? [li, 0, layer, lp, beat] : [li, 1, 0, ep, beat];
}

function compareRank(a: readonly number[], b: readonly number[]): number {
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export interface BezierCollision {
  /** Start key of the other curve's segment. */
  readonly key: KeyId;
  readonly line: LineId;
  readonly prop: LineProp;
  readonly p: BezierPoints;
}

export interface CollisionReport {
  readonly collisions: readonly BezierCollision[];
  /**
   * The handles Phira will actually use for this curve: its own when it comes first in the scan, else those of the
   * first colliding curve. Null when there is no collision.
   */
  readonly drawnWith: BezierPoints | null;
  /** Phira's rounded key (diagnostics). */
  readonly key: string;
}

/**
 * Other Bezier curves of the chart whose Phira key equals that of `p` while their handles differ. `segments` are the
 * segments the curve is meant for (left out of the scan; the earliest one decides who comes first).
 */
export function bezierCollisions(
  doc: ChartDoc,
  idx: DocIndexes,
  p: BezierPoints,
  segments: readonly KeyId[],
): CollisionReport {
  const key = Ease.bezierKey(p);
  const own = new Set(segments);
  let ownRank: readonly number[] | null = null;
  for (const id of segments) {
    const r = scanRank(doc, idx, id);
    if (r !== null && (ownRank === null || compareRank(r, ownRank) < 0)) ownRank = r;
  }
  const hits: { c: BezierCollision; rank: readonly number[] }[] = [];
  for (const k of doc.keys.values()) {
    if (own.has(k.id) || k.ease.kind !== 'bezier') continue;
    if (idx.nextKey(k.id) === undefined) continue;
    const rank = scanRank(doc, idx, k.id);
    if (rank === null) continue;
    if (Ease.bezierKey(k.ease.p) !== key || sameHandles(k.ease.p, p)) continue;
    const t = doc.tracks.get(k.track);
    if (t?.owner.kind !== 'line') continue;
    hits.push({ c: { key: k.id, line: t.owner.line, prop: t.owner.prop, p: k.ease.p }, rank });
  }
  hits.sort((a, b) => compareRank(a.rank, b.rank));
  const first = hits[0];
  if (first === undefined) return { collisions: [], drawnWith: null, key };
  const otherFirst = ownRank === null || compareRank(first.rank, ownRank) < 0;
  return { collisions: hits.map((h) => h.c), drawnWith: otherFirst ? first.c.p : p, key };
}
